import './antConfirmModal.less';
import React, { FunctionComponent } from 'react';
import { AntModal, createContainer, ModalPosition, AntModalProps } from './antModal';
import { Section } from 'controls/react/layout/section';
import classnames from 'classnames';
import { CloseCircleFilled, InfoCircleFilled, QuestionCircleFilled, WarningFilled } from '@ant-design/icons';
import { Observer } from 'mobx-react';
import { State } from 'tools/state';
import b_ from 'b_';
import { translator } from 'core/localization';

const b = b_.with('ant-confirm-modal');
import { i18n } from 'core/localization';

export enum IconType {
	Info = 'INFO',
	Warning = 'WARNING',
	Error = 'ERROR',
	Question = 'QUESTION',
}

export type AsyncModalFuncResult = Promise<boolean> | Promise<void> | void;
export interface AntConfirmModalProps {
	onCancel?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
	onOk?: (e?: React.MouseEvent<HTMLButtonElement>) => AsyncModalFuncResult;
	title?: string;
	okText?: string;
	icon?: IconType;
	message: string;
	className?: string;
	positionType?: ModalPosition;
	draggableHandle?: string;
	positionAnchor?: React.MutableRefObject<HTMLElement>;
	defaultFooterButtons?: ('ok' | 'cancel')[];
}

export const AntConfirmModal: FunctionComponent<AntConfirmModalProps & AntModalProps> = (props) => {
	const { className, okText, title, onCancel, onOk, positionType, icon, message, ...others } = props;
	const classes = classnames(className ?? '', b());
	return (
		<AntModal
			okText={okText ?? i18n('Ok')}
			title={title ?? i18n('Information')}
			open={true}
			onCancel={onCancel}
			onOk={onOk}
			resizable={props.resizable === undefined || props.resizable === null ? false : props.resizable}
			positionType={positionType}
			className={classes}
			{...others}
		>
			<Section>
				<Icon icon={icon ?? IconType.Info} /> {message}
			</Section>
		</AntModal>
	);
};

const Icon: FunctionComponent<{ icon: IconType }> = (props: { icon: IconType }) => {
	const mod: { [id: string]: boolean } = {};
	mod[props.icon.toLowerCase()] = true;
	switch (props.icon) {
		case IconType.Info: {
			return <InfoCircleFilled className={b('icon', mod)} />;
		}
		case IconType.Question: {
			return <QuestionCircleFilled className={b('icon', mod)} />;
		}
		case IconType.Warning: {
			return <WarningFilled className={b('icon', mod)} />;
		}
		case IconType.Error: {
			return <CloseCircleFilled className={b('icon', mod)} />;
		}
		default: {
			return null;
		}
	}
};

export function openConfirmMessage(props: (() => AntConfirmModalProps) | AntConfirmModalProps) {
	return new Promise<boolean>((resolve) => {
		const [root, destroyContainer] = createContainer();

		const getProps = (p: (() => AntConfirmModalProps) | AntConfirmModalProps) => {
			if (typeof p == 'function') {
				return p();
			}
			return p;
		};

		root.render(
			<>
				<Observer>
					{() => {
						const { onCancel, onOk, ...restProps } = getProps(props);

						const onCancelWrapper = () => {
							destroyContainer();
							onCancel?.();
							resolve(false);
						};
						const onOkWrapper: (e?: React.MouseEvent<HTMLButtonElement>) => AsyncModalFuncResult = async (
							e?: React.MouseEvent<HTMLButtonElement>,
						) => {
							destroyContainer();
							if (onOk) {
								try {
									const result = await onOk(e);
									resolve(result === true || result === false ? result : true);
									return !!result;
								} catch {
									resolve(false);
								}
							} else {
								resolve(true);
							}
							return true;
						};

						return <AntConfirmModal onOk={onOkWrapper} onCancel={onCancelWrapper} {...restProps} />;
					}}
				</Observer>
			</>,
		);

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
		State.mainApp.registerForOnNavigate(() => {
			root.unmount();
			resolve(false);
		});
	});
}

export const openDeleteConfirmMessage = (props: {
	message: string;
	onOk: (e?: React.MouseEvent<HTMLButtonElement>) => AsyncModalFuncResult;
}) =>
	openConfirmMessage({
		title: i18n('Info'),
		icon: IconType.Error,
		message: props.message,
		positionType: ModalPosition.Mouse,
		onOk: props.onOk,
		okText: i18n('Delete'),
	});
